import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/dashboard/layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AccountSettings } from "@/components/settings/AccountSettings";
import { PaymentsTab } from "@/components/settings/PaymentsTab";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CreditCard, UserCircle } from "lucide-react";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("account");
  const queryClient = useQueryClient();
  
  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    staleTime: 0, // Always refetch
    refetchOnWindowFocus: true,
  });

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['userProfile', userData?.id],
    queryFn: async () => {
      if (!userData) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userData.id)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
        return null;
      }
      
      return data;
    },
    staleTime: 0, // Always refetch to ensure latest data
    enabled: !!userData?.id,
  });

  useEffect(() => {
    if (profile) {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    }
  }, [profile, queryClient]);

  const getSocialLinks = (): Record<string, string> | null => {
    if (!profile || !profile.socials) return null;
    
    if (typeof profile.socials === 'object' && profile.socials !== null) {
      const validSocials: Record<string, string> = {};
      
      Object.entries(profile.socials).forEach(([key, value]) => {
        if (typeof value === 'string') {
          validSocials[key] = value;
        }
      });
      
      return Object.keys(validSocials).length > 0 ? validSocials : null;
    }
    
    return null;
  };

  return (
    <DashboardLayout
      title="Settings"
      description="Manage your account settings and creator profile"
    >
      <div className="container max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 gap-8">
          <div>
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="w-full mb-8">
                <TabsTrigger value="account" className="flex items-center gap-2 w-full">
                  <UserCircle size={16} />
                  <span>Account</span>
                </TabsTrigger>
                {profile?.role === 'creator' && (
                  <TabsTrigger value="payments" className="flex items-center gap-2 w-full">
                    <CreditCard size={16} />
                    <span>Payments</span>
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="account">
                <AccountSettings 
                  profile={profile} 
                  socialLinks={getSocialLinks()}
                  hideAccountRole={true}
                />
              </TabsContent>
              
              {profile?.role === 'creator' && (
                <TabsContent value="payments">
                  <PaymentsTab />
                </TabsContent>
              )}
            </Tabs>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
